import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { industries } from '../../data/mock';
import { toast } from 'sonner';
import { auditAPI } from '../../services/api';

const CALENDAR_URL = 'https://zcal.co/dibum/30min';

const AuditFormSection = ({ variant = 'default' }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    websiteUrl: '',
    email: '',
    phone: '',
    industry: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, industry: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await auditAPI.createSubmission({
        ...formData,
        businessName: formData.fullName,
        location: 'Not specified',
        challenge: 'Free audit request'
      });
      setIsSubmitted(true);
      setShowScheduleModal(true);
      toast.success('Your audit request has been submitted successfully!');
    } catch (error) {
      console.error('Error submitting audit request:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleCall = () => {
    window.open(CALENDAR_URL, '_blank');
    setShowScheduleModal(false);
  };

  const handleSkipSchedule = () => {
    setShowScheduleModal(false);
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({ fullName: '', websiteUrl: '', email: '', phone: '', industry: '' });
  };

  if (isSubmitted) {
    return (
      <>
        {/* Schedule Call Modal */}
        <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center text-xl">
                🎉 Audit Request Submitted!
              </DialogTitle>
              <DialogDescription className="text-center">
                Our team will review your request and send your free audit within 48 hours.
              </DialogDescription>
            </DialogHeader>
            <div className="py-6 text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <div className="space-y-2">
                <p className="text-slate-700 font-medium">
                  Want faster results?
                </p>
                <p className="text-slate-500 text-sm">
                  Schedule a free 30-minute strategy call to discuss your AI visibility goals.
                </p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <Button
                  onClick={handleScheduleCall}
                  className="w-full h-12 bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 hover:from-blue-700 hover:via-violet-700 hover:to-pink-600 text-white font-semibold"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule a Free Call
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleSkipSchedule}
                  className="text-slate-500 hover:text-slate-700"
                >
                  I&apos;ll wait for the audit report
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <div className="bg-white rounded-3xl p-8 shadow-2xl shadow-violet-500/10 border border-violet-100 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            Thank You!
          </h3>
          <p className="text-slate-600 mb-4 text-sm">
            Our team will review and share your audit report within 48 hours.
          </p>
          <div className="space-y-3">
            <Button
              onClick={handleScheduleCall}
              className="w-full bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 text-white"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Schedule a Strategy Call
            </Button>
            <Button
              onClick={resetForm}
              variant="outline"
              size="sm"
              className="border-violet-200 text-violet-700 hover:bg-violet-50"
            >
              Submit Another
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Compact version for hero section
  if (variant === 'compact') {
    return (
      <div className="bg-white rounded-3xl p-6 shadow-2xl shadow-violet-500/10 border border-slate-100">
        <h3 className="text-lg font-bold text-slate-900 mb-1">Get Your Free 48H Audit</h3>
        <p className="text-sm text-slate-500 mb-4">Discover how your brand appears in AI search results</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            placeholder="Your full name *"
            required
            className="h-11 rounded-xl border-slate-200 focus:border-violet-300 focus:ring-violet-200"
          />
          <Input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Please use your company email *"
            required
            className="h-11 rounded-xl border-slate-200 focus:border-violet-300 focus:ring-violet-200"
          />
          <Input
            name="websiteUrl"
            value={formData.websiteUrl}
            onChange={handleInputChange}
            placeholder="e.g., pixzent.com or https://pixzent.com *"
            required
            className="h-11 rounded-xl border-slate-200 focus:border-violet-300 focus:ring-violet-200"
          />
          <Input
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Phone number *"
            required
            className="h-11 rounded-xl border-slate-200 focus:border-violet-300 focus:ring-violet-200"
          />
          <Select value={formData.industry} onValueChange={handleSelectChange} required>
            <SelectTrigger className="h-11 rounded-xl border-slate-200 focus:border-violet-300">
              <SelectValue placeholder="Select your industry *" />
            </SelectTrigger>
            <SelectContent>
              {industries.map((industry) => (
                <SelectItem key={industry} value={industry}>
                  {industry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-11 bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 hover:from-blue-700 hover:via-violet-700 hover:to-pink-600 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25"
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Get My Free Audit'
            )}
          </Button>
          <p className="text-center text-xs text-slate-500">
            No credit card required • Results in 48 hours
          </p>
        </form>
      </div>
    );
  }

  // Full version
  return (
    <section id="audit" className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-violet-50/50 to-white" />
      
      {/* Decorative */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-pink-400/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">
                Limited Spots Available This Month
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
                Get Your Free{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500">
                  48-Hour AI Audit
                </span>
              </h2>
              <p className="text-lg text-slate-600">
                Discover exactly how your brand appears in AI search results and get a custom roadmap to dominate your industry.
              </p>
            </div>

            <ul className="space-y-4">
              {[
                'Complete AI visibility audit across ChatGPT, Perplexity & Grok',
                'Competitor analysis and gap identification',
                'Custom optimization roadmap for your industry',
                'Actionable recommendations you can implement today'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>

            {/* Trust Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: '100+', label: 'Brands Helped' },
                { value: '847%', label: 'Avg ROI' },
                { value: '94%', label: 'Retention' }
              ].map((stat, index) => (
                <div key={index} className="text-center p-4 bg-white/60 rounded-2xl border border-slate-100">
                  <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600">
                    {stat.value}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl shadow-violet-500/10 border border-slate-100">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                <Input
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="John Smith"
                  required
                  className="h-12 rounded-xl border-slate-200 focus:border-violet-300 focus:ring-violet-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Website URL *</label>
                <Input
                  name="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={handleInputChange}
                  placeholder="e.g., pixzent.com or https://pixzent.com"
                  required
                  className="h-12 rounded-xl border-slate-200 focus:border-violet-300 focus:ring-violet-200"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@company.com"
                    required
                    className="h-12 rounded-xl border-slate-200 focus:border-violet-300 focus:ring-violet-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone *</label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+971 50 123 4567"
                    required
                    className="h-12 rounded-xl border-slate-200 focus:border-violet-300 focus:ring-violet-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Industry *</label>
                <Select value={formData.industry} onValueChange={handleSelectChange} required>
                  <SelectTrigger className="h-12 rounded-xl border-slate-200 focus:border-violet-300 focus:ring-violet-200">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 hover:from-blue-700 hover:via-violet-700 hover:to-pink-600 text-white text-lg font-semibold rounded-xl shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Get My Free Audit
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-slate-500">
                No credit card required • Results in 48 hours
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AuditFormSection;
